<template>
	<Dialog
		:visible="visible"
		:modal="true"
		:closable="false"
		:dismissableMask="false"
		:closeOnEscape="false"
		:draggable="false"
		:baseZIndex="5000"
		:blockScroll="true"
		:style="{ width: '22rem' }"
	>
		<template #header>
			<span class="text-lg font-semibold">{{ t("content.exporting_event_content") }}</span>
		</template>
		<div class="flex flex-col items-center gap-4 py-2">
			<p
				v-if="statusLabel"
				class="text-center text-surface-700 dark:text-surface-100 m-0 text-sm"
			>
				{{ statusLabel }}
			</p>
			<div
				v-if="visible"
				class="relative h-2 w-full overflow-hidden rounded-full bg-gray-200"
			>
				<div
					v-if="showDeterminateProgress"
					class="h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
					:style="{ width: `${progressPercent}%` }"
				/>
				<div
					v-else
					class="progress-bar-indeterminate h-full w-2/5 rounded-full bg-primary"
				/>
			</div>
			<p
				v-if="visible && total > 0"
				class="text-sm text-surface-600 m-0"
			>
				{{ processed }} / {{ total }}
			</p>
			<p
				v-if="visible && remaining > 0"
				class="text-sm text-surface-500 m-0"
			>
				{{ remaining }} {{ t("content.files_remaining") }}
			</p>
		</div>
	</Dialog>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import Dialog from "primevue/dialog";
import { useEventsStore } from "@/stores/apps/events";
import { useStoreI18n } from "@/composables/useStoreI18n";

const { t } = useStoreI18n();
const eventsStore = useEventsStore();

const visible = computed(() => eventsStore.eventContentExportVisible);
const total = computed(() => eventsStore.eventContentExportTotal);
const processed = computed(() => eventsStore.eventContentExportProcessed);
const remaining = computed(() => eventsStore.eventContentExportRemaining);
const status = computed(() => eventsStore.eventContentExportStatus);
const showDeterminateProgress = computed(
	() =>
		total.value > 0 &&
		!["transferring", "ready", "uploading"].includes(status.value)
);

const progressPercent = computed(() => {
	if (total.value <= 0) return 0;
	return Math.min(100, Math.round((processed.value / total.value) * 1000) / 10);
});

const statusLabel = computed(() => {
	if (status.value === "uploading") {
		return t("content.export_uploading_s3");
	}
	if (status.value === "ready") {
		return t("content.export_ready_downloading");
	}
	if (status.value === "transferring") {
		return t("content.export_transferring_zip");
	}
	return "";
});
</script>

<style scoped>
.progress-bar-indeterminate {
	animation: event-content-export-indeterminate 1.2s ease-in-out infinite;
}

@keyframes event-content-export-indeterminate {
	0% {
		transform: translateX(-100%);
	}
	100% {
		transform: translateX(350%);
	}
}
</style>
